import React from "react";
import { ArrowRight, ShieldCheck, Lightning, Users, Star } from "@phosphor-icons/react";

const HERO_BG =
  "https://images.unsplash.com/photo-1762279389042-9439bfb6c155?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MjJ8MHwxfHNlYXJjaHwyfHxhYnN0cmFjdCUyMGRhcmslMjB0ZWNobm9sb2d5JTIwYmFja2dyb3VuZHxlbnwwfHx8fDE3Nzc2ODk1MjZ8MA&ixlib=rb-4.1.0&q=85";

export default function Hero({ business, topOffset = 0, onBook }) {
  return (
    <section
      id="top"
      data-testid="hero-section"
      style={{ paddingTop: `calc(7rem + ${topOffset}px)` }}
      className="relative bg-zinc-950 text-white overflow-hidden md:!pt-36"
    >
      <img
        src={HERO_BG}
        alt="Abstract technology background"
        className="absolute inset-0 w-full h-full object-cover opacity-60"
        loading="eager"
      />
      <div className="absolute inset-0 bg-gradient-to-b from-zinc-950/55 via-zinc-950/75 to-zinc-950" />
      <div className="absolute inset-0 hero-grid" />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-32 md:pb-48">
        <div className="max-w-3xl animate-fade-up">
          <div className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/5 px-3 py-1 text-xs tracking-widest uppercase text-blue-300">
            <ShieldCheck size={14} weight="fill" /> Certified IT Specialists
          </div>

          <h1
            data-testid="home-h1"
            className="mt-6 font-heading font-extrabold tracking-tighter text-white text-4xl sm:text-5xl lg:text-6xl leading-[1.05]"
          >
            Computer Repair &amp; IT Support in North Carolina
          </h1>
          <div
            data-testid="home-hero-tagline"
            className="mt-4 font-heading font-extrabold tracking-tighter text-white text-3xl sm:text-4xl lg:text-5xl leading-[1.1]"
          >
            Slow PC? Virus scare?
            <span className="block text-blue-500">We fix it. Fast.</span>
          </div>

          <p className="mt-6 text-lg text-zinc-300 max-w-2xl leading-relaxed">
            Professional PC tune-ups, malware & virus removal, data backup, and managed business IT — serving all of North Carolina. Same-day diagnostics. Nationwide remote support. No-fix-no-fee.
          </p>

          <div className="mt-8 flex flex-wrap items-center gap-3">
            <button
              onClick={() => onBook && onBook()}
              data-testid="cta-hero-book"
              className="group inline-flex items-center gap-2 rounded-full bg-blue-600 hover:bg-blue-700 text-white px-6 py-3.5 font-semibold shadow-lg shadow-blue-600/30 transition-all"
            >
              Book free diagnostic
              <ArrowRight size={18} weight="bold" className="transition-transform group-hover:translate-x-1" />
            </button>
            <a
              href="#contact"
              data-testid="cta-hero-quote"
              className="inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/5 hover:bg-white/10 text-white px-6 py-3.5 font-semibold transition-all"
            >
              Get a free quote
            </a>
            <a
              href={`tel:${business?.phoneTel || "+19197375442"}`}
              data-testid="cta-hero-emergency"
              className="inline-flex items-center gap-2 rounded-full bg-red-600/20 hover:bg-red-600/30 text-red-300 border border-red-500/30 px-5 py-3.5 font-semibold transition-all"
            >
              <span className="relative flex h-2 w-2"><span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75"></span><span className="relative inline-flex rounded-full h-2 w-2 bg-red-500"></span></span>
              Emergency IT help
            </a>
            <a
              href={`tel:${business?.phoneTel || "+19197375442"}`}
              data-testid="cta-hero-call"
              className="inline-flex items-center gap-2 text-white/90 hover:text-white font-semibold px-2"
            >
              or call {business?.phone}
            </a>
          </div>

          <div className="mt-8 flex items-center gap-2 text-sm text-zinc-400">
            <div className="flex -space-x-2">
              {[1,2,3,4].map(i => (
                <div key={i} className="h-7 w-7 rounded-full border-2 border-zinc-950 bg-gradient-to-br from-zinc-700 to-zinc-900" />
              ))}
            </div>
            <span>Trusted by 1,200+ customers</span>
          </div>
        </div>
      </div>

      {/* Trust bar */}
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 -mb-16 md:-mb-20 z-10">
        <div data-testid="hero-trust-bar" className="grid grid-cols-2 md:grid-cols-4 bg-white rounded-2xl shadow-2xl shadow-zinc-950/30 border border-zinc-200 overflow-hidden">
          {[
            { icon: <Users size={22} weight="duotone" />, k: "12,000+", v: "Devices serviced" },
            { icon: <Star size={22} weight="duotone" />, k: "Same-day", v: "Diagnostics" },
            { icon: <Lightning size={22} weight="duotone" />, k: "< 24 hrs", v: "Turnaround time" },
            { icon: <ShieldCheck size={22} weight="duotone" />, k: "15+ yrs", v: "In business" },
          ].map((s, i) => (
            <div key={i} className="p-6 md:p-8 border-r last:border-r-0 border-zinc-200 flex flex-col">
              <div className="text-blue-600">{s.icon}</div>
              <div className="mt-3 font-heading text-2xl md:text-3xl font-extrabold text-zinc-900">{s.k}</div>
              <div className="text-sm text-zinc-500 mt-1">{s.v}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
